import { DragDropModule } from "@angular/cdk/drag-drop";
import { HashLocationStrategy, LocationStrategy } from "@angular/common";
import {
    HTTP_INTERCEPTORS,
    provideHttpClient,
    withInterceptorsFromDi,
    withXhr,
} from "@angular/common/http";
import {
    CUSTOM_ELEMENTS_SCHEMA,
    NgModule,
    effect,
    inject,
} from "@angular/core";
import { ReactiveFormsModule } from "@angular/forms";
import { BrowserModule } from "@angular/platform-browser";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import {
    BadgeModule,
    BreadcrumbRouterComponent,
    ButtonGroupModule,
    ButtonModule,
    CardModule,
    DropdownModule,
    FooterModule,
    FormModule,
    GridModule,
    HeaderModule,
    ListGroupModule,
    NavModule,
    ProgressModule,
    SharedModule,
    SidebarModule,
    TabsModule,
    UtilitiesModule,
} from "@coreui/angular";
import { IconModule, IconSetService } from "@coreui/icons-angular";
import { AkitaNgDevtools } from "@datorama/akita-ngdevtools";
import { API_BASE_URL, withFirm360Interceptors } from "@firm360/libs/api/http";
import { AuthTokenService, provideAuth } from "@firm360/libs/auth";
import { provideCurrentIdentity } from "@firm360/libs/domain/core";
import { RateLimitBannerComponent } from "@firm360/libs/errors";
import { providePendo } from "@firm360/libs/observability/pendo";
import {
    ToastContainer,
    ToastService,
    provideLumina,
} from "@firm360/libs/ui/lumina";
import { ModalService } from "@firm360/libs/ui/modal";
import {
    NgbDateParserFormatter,
    NgbDatepickerConfig,
    NgbInputDatepickerConfig,
    NgbModal,
    NgbModule,
} from "@ng-bootstrap/ng-bootstrap";
import moment from "moment";
import { NgxMaskDirective, provideNgxMask } from "ngx-mask";
import { NgScrollbarModule } from "ngx-scrollbar";
import {
    provideNgxWebstorage,
    withLocalStorage,
    withNgxWebstorageConfig,
    withSessionStorage,
} from "ngx-webstorage";
import { environment } from "../environments/environment";
import { Firm360AccountModule } from "./account/account.module";
import { AppComponent } from "./app.component";
// Import layout
import { AppRoutingModule } from "./app.routing";
import { AuthInterceptor } from "./blocks/interceptor/auth.interceptor";
import { Firm360CoreModule } from "./core";
import { SessionExpirationService } from "./core/auth/session-expiration.service";
import { HeaderTimerComponent } from "./entities/time-tracking/header-timer/header-timer.component";
import {
    DefaultFooterComponent,
    DefaultHeaderComponent,
    DefaultLayoutComponent,
} from "./layout";
import { Firm360SharedModule } from "./shared/shared.module";
import { NgbDateUSParserFormatter } from "./shared/util/ngb-date-us-parser-formatter";
import { P404Component } from "./views/error/404.component";
import { P500Component } from "./views/error/500.component";
// Import routing module

const APP_CONTAINERS = [
    DefaultLayoutComponent,
    DefaultHeaderComponent,
    DefaultFooterComponent,
];

@NgModule({
    declarations: [
        AppComponent,
        ...APP_CONTAINERS,
        P404Component,
        P500Component,
    ],
    bootstrap: [AppComponent],
    exports: [],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    imports: [
        BrowserModule,
        Firm360SharedModule.forRoot(),
        Firm360CoreModule,
        Firm360AccountModule,
        BrowserAnimationsModule,
        AppRoutingModule,
        NgbModule,
        DragDropModule,
        NgxMaskDirective,
        environment.production ? [] : AkitaNgDevtools.forRoot(),
        FooterModule,
        DropdownModule,
        GridModule,
        HeaderModule,
        SidebarModule,
        NavModule,
        ButtonModule,
        FormModule,
        UtilitiesModule,
        ButtonGroupModule,
        ReactiveFormsModule,
        SharedModule,
        TabsModule,
        ProgressModule,
        BadgeModule,
        ListGroupModule,
        CardModule,
        IconModule,
        BreadcrumbRouterComponent,
        NgScrollbarModule,
        RateLimitBannerComponent,
        ToastContainer,
        HeaderTimerComponent,
    ],
    providers: [
        provideNgxWebstorage(
            withNgxWebstorageConfig({
                prefix: "jhi",
                separator: "-",
                caseSensitive: false,
            }),
            withLocalStorage(),
            withSessionStorage(),
        ),
        provideLumina(),
        // The app-level toast stack, rendered by the container in the default
        // layout. Root-scoped so anything reaches it — including ngb modal
        // content, which is created off the root injector — and so a toast
        // outlives the view that raised it. A view that wants its toasts to read
        // as part of its own surface provides ToastService itself instead.
        ToastService,
        provideAuth({ realm: "firm" }),
        provideCurrentIdentity(),
        providePendo({ apiKey: environment.pendoApiKey }),
        { provide: API_BASE_URL, useValue: environment.apiUrl },
        {
            provide: LocationStrategy,
            useClass: HashLocationStrategy,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: AuthInterceptor,
            multi: true,
        },
        { provide: NgbDateParserFormatter, useClass: NgbDateUSParserFormatter },
        IconSetService,
        provideNgxMask(),
        { provide: NgbModal, useClass: ModalService },
        provideHttpClient(
            withXhr(),
            withInterceptorsFromDi(),
            withFirm360Interceptors({
                unauthenticated: {
                    logout: () => inject(SessionExpirationService).expire(),
                    ignoredApiPaths: [
                        "api/authenticate",
                        "api/account",
                        "api/firm-custom-logo",
                    ],
                },
                legacyErrorHandling: {
                    serverErrorPage: "/#/500",
                    notFoundPage: (error) =>
                        error.url?.includes("user-tokens")
                            ? "/#/404/expired-link"
                            : "/#/404/",
                    ignoredRoutes: ["/500"],
                },
            }),
        ),
    ],
})
export class AppModule {
    constructor() {
        const dpConfig = inject(NgbDatepickerConfig);
        const inputDpConfig = inject(NgbInputDatepickerConfig);

        for (const config of [dpConfig, inputDpConfig]) {
            config.minDate = { year: moment().year() - 100, month: 1, day: 1 };
            config.firstDayOfWeek = 7;
        }

        // The root toast queue outlives every view on purpose (see its provider
        // above), which means it also outlives a login: a sticky error toast's
        // Retry button can otherwise fire after a logout, expiry, or
        // impersonation switch, replaying a stale request under whatever
        // session happens to be current by then. Clearing it on every token
        // change closes that for all three at once, at the one place they all
        // already funnel through.
        const authToken = inject(AuthTokenService, { optional: true });
        const toast = inject(ToastService);
        effect(() => {
            authToken?.token();
            toast.clear();
        });
    }
}
